import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import React, { FC, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { UserProfile } from '../../@posturinn/client-react';
import { logout } from '../../actions/userProfileActions';
import { useDevice } from '../../constants/device/device';
import { firstName } from '../../helpers/stringHelpers/stringHelpers';
import { getHomeUrl } from '../../helpers/url/urlHelpers';
import useKeyboardEvent from '../../hooks/useKeyboardEvent';
import { ReactComponent as ArrowRight } from '../../icons/arrow-right-2.svg';
import { ReactComponent as CloseButton } from '../../icons/close-button.svg';
import logoLeft from '../../icons/logo_left.svg';
import IUserProfileReducer from '../../interfaces/reducers/userProfileReducer';
import IAppFrameViewModel, { IHeaderMainMenuItem } from '../../interfaces/umbracoNodes/appFrameViewModel';
import { IStore } from '../../reducers';
import { RootState } from '../../store';
import RichTextTitle from '../base/richTextTitle';
import SVG from '../rawSVG';
import AnnouncementBanner from './announcementBanner';
import * as StyledHeader from './header.styles';
import { NavigationWrapper, PageContainerStyled, PrimaryButton, PrimaryMenu } from './header.styles';
import StickyHeader from './stickyHeader';

const Header: FC = () => {
	const { appFrame }: { appFrame: IAppFrameViewModel } = useSelector((state: RootState) => state.pageReducer);
	const { t } = useTranslation(['myShipments']);
	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { userProfile, isLoggedIn }: { userProfile: UserProfile; isLoggedIn: boolean } = userProfileReducer;
	const location = useLocation();
	const dispatch = useDispatch<any>();
	const [stickyHeaderVisible, setStickyHeaderVisible] = useState<boolean | undefined>(undefined);
	const [selectedMainMenu, setSelectedMainMenu] = useState<IHeaderMainMenuItem | null>(null);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [hoverMode, setHoverMode] = useState(true);

	const dropdownRef = useRef<HTMLDivElement>(null);
	const firstFocusableDropdownRef = useRef<HTMLAnchorElement>(null);
	const { smallDevice } = useDevice();

	const removalTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	useKeyboardEvent('Escape', () => {
		if (!hoverMode) {
			setSelectedMainMenu(null);
			setHoverMode(true);
		}
	});

	useEffect(() => {
		setMobileMenuOpen(false);
		setSelectedMainMenu(null);
		setHoverMode(true);
		clearAllBodyScrollLocks();
	}, [location, smallDevice]);

	useEffect(() => {
		if (!!selectedMainMenu && !hoverMode && firstFocusableDropdownRef.current) {
			firstFocusableDropdownRef.current.focus();
		}
	}, [hoverMode, selectedMainMenu]);

	const onPrimaryNavClick = (idx: number) => {
		if (!hoverMode && selectedMainMenu.Values.Title !== appFrame.Values.HeaderMainMenu[idx].Values.Title) {
			setSelectedMainMenu(appFrame.Values.HeaderMainMenu[idx]);
			return;
		}
		setHoverMode(!hoverMode);
	};

	const onOverlayClick = () => {
		setHoverMode(true);
		setSelectedMainMenu(null);
	};

	const onFirstPrimaryNavFocus = () => {
		setStickyHeaderVisible(true);
	};

	const onStickyHeaderVisibilityChange = useCallback((state?: boolean) => {
		setStickyHeaderVisible(state);
	}, []);

	const onScoll = useCallback(() => {
		if (!!selectedMainMenu) {
			setSelectedMainMenu(null);
			setHoverMode(true);
		}
	}, [selectedMainMenu]);

	const onPrimaryNavKeyDown = (event: KeyboardEvent, idx: number) => {
		if (event.keyCode === 13 || event.keyCode === 32) {
			event.preventDefault();
			setHoverMode(
				!!selectedMainMenu && selectedMainMenu.Values.Title === appFrame.Values.HeaderMainMenu[idx].Values.Title
			);
			onMobilePrimaryNavClick(idx);
		}
	};

	const onFirstFocusableDropdownRefKeyDown = (event: KeyboardEvent) => {
		if (event.keyCode === 9 && event.shiftKey) {
			event.preventDefault();
			setHoverMode(true);
			setSelectedMainMenu(null);
			(
				document.querySelector(
					`header button[aria-label="${selectedMainMenu.Values.Title}"]`
				) as HTMLButtonElement
			).focus();
		}
	};

	const onLastFocusableDropdownRefKeyDown = (event: KeyboardEvent) => {
		if (event.keyCode === 9 && !event.shiftKey) {
			event.preventDefault();
			setHoverMode(true);
			setSelectedMainMenu(null);
			(
				document.querySelector(`header button[aria-label="${selectedMainMenu.Values.Title}"]`).nextSibling as
					| HTMLButtonElement
					| HTMLAnchorElement
			).focus();
		}
	};

	const onPrimaryNavMouseEnter = (idx: number) => {
		if (!hoverMode) {
			return;
		}
		clearTimeout(removalTimeoutRef.current);
		setSelectedMainMenu(appFrame.Values.HeaderMainMenu[idx]);
	};

	const removeSelectedMenu = () => {
		if (!hoverMode) {
			return;
		}
		removalTimeoutRef.current = setTimeout(() => {
			setSelectedMainMenu(null);
		}, 400);
	};

	const onPrimaryNavMouseLeave = () => {
		removeSelectedMenu();
	};

	const onDropdownMouseEnter = () => {
		if (!hoverMode) {
			return;
		}
		clearTimeout(removalTimeoutRef.current);
	};

	const onDropdownMouseLeave = () => {
		removeSelectedMenu();
	};

	const onMobileMenuCLick = () => {
		if (mobileMenuOpen) {
			clearAllBodyScrollLocks();
		} else {
			disableBodyScroll(dropdownRef.current);
		}
		setSelectedMainMenu(null);
		setMobileMenuOpen(!mobileMenuOpen);
	};

	const onMobilePrimaryNavClick = (idx: number) => {
		setSelectedMainMenu(prev =>
			prev && prev.Values.Title === appFrame.Values.HeaderMainMenu[idx].Values.Title
				? null
				: appFrame.Values.HeaderMainMenu[idx]
		);
	};

	const onCloseMenuClick = () => {
		clearAllBodyScrollLocks();
		setSelectedMainMenu(null);
		setMobileMenuOpen(false);
	};
	return (
		<>
			<StickyHeader
				stickyHeaderVisible={stickyHeaderVisible}
				onStickyHeaderVisibilityChange={onStickyHeaderVisibilityChange}
				onScroll={onScoll}>
				<StyledHeader.HeaderContainer>
					{!smallDevice && selectedMainMenu && (
						<StyledHeader.PageOverlay onClick={!hoverMode ? onOverlayClick : null} />
					)}
					<PageContainerStyled>
						<NavigationWrapper>
							<PrimaryMenu role="menubar">
								{smallDevice && mobileMenuOpen ? (
									<StyledHeader.CloseMenuButton
										aria-label="Close menu"
										id="closebutton"
										onClick={onCloseMenuClick}>
										<CloseButton></CloseButton>
									</StyledHeader.CloseMenuButton>
								) : smallDevice ? (
									<StyledHeader.BurgerMenuButton
										aria-expanded={mobileMenuOpen}
										onClick={onMobileMenuCLick}
										id="menubutton"
										aria-haspopup="true">
										<StyledHeader.MenuButtonCenter />
									</StyledHeader.BurgerMenuButton>
								) : (
									''
								)}
								{!smallDevice && (
									<>
										{appFrame.Values.HeaderMainMenu.map((menu, idx) => (
											<PrimaryButton
												key={idx}
												onClick={() => onPrimaryNavClick(idx)}
												onMouseEnter={() => onPrimaryNavMouseEnter(idx)}
												onMouseLeave={onPrimaryNavMouseLeave}
												onKeyDown={event => onPrimaryNavKeyDown(event, idx)}
												onFocus={idx === 0 ? onFirstPrimaryNavFocus : null}
												aria-label={menu.Values.Title}
												aria-expanded={
													!!selectedMainMenu &&
													selectedMainMenu.Values.Title === menu.Values.Title
												}
												aria-haspopup="true">
												{menu.Values.Title}
											</PrimaryButton>
										))}
										<StyledHeader.SearchLink
											href={appFrame.Values.SearchLink.Url}
											title={appFrame.Values.SearchLink.Name}
											target={appFrame.Values.SearchLink.Target}
											icon={appFrame.Values.SearchLinkIcon}
										/>
									</>
								)}
							</PrimaryMenu>
							<StyledHeader.SecondaryMenu>
								{!smallDevice && (
									<StyledHeader.MyAccountDropdownMenu>
										<StyledHeader.MyAccountDropdownHeader>
											{
												<SVG
													src={appFrame.Values.MyAccountLinkIcon.Src}
													width={24}
													height={24}
													title={appFrame.Values.MyAccountLinkIcon.Name}
												/>
											}
											{isLoggedIn && userProfile
												? `${t('myShipments:hi')} ${firstName(userProfile.name)}`
												: appFrame.Values.AccountMenuName}
										</StyledHeader.MyAccountDropdownHeader>
										<StyledHeader.MyAccountDropdownContent>
											{appFrame.Values.AccountMenuLinks.map((link, idx) => (
												<StyledHeader.MyAccountDropDownLink
													key={idx}
													href={link.Values.Item.Url}>
													<span>{link.Values.Item.Name}</span> {<ArrowRight />}
												</StyledHeader.MyAccountDropDownLink>
											))}
										</StyledHeader.MyAccountDropdownContent>
									</StyledHeader.MyAccountDropdownMenu>
								)}
								<StyledHeader.LanguageSwitch />
								<StyledHeader.LogoWrapper id="logo" to={getHomeUrl()} aria-label="frontpage">
									<img src={logoLeft} alt="Posturinn" />
								</StyledHeader.LogoWrapper>
							</StyledHeader.SecondaryMenu>
						</NavigationWrapper>
						{smallDevice && (
							<StyledHeader.MenuItemWrapper mobileMenuOpen={mobileMenuOpen} aria-labelledby="menubutton">
								<StyledHeader.NavigationDropdown ref={dropdownRef}>
									{mobileMenuOpen && (
										<>
											<StyledHeader.DropdownTopSection>
												{appFrame.Values.HeaderMainMenu.map((menu, idx) => (
													<StyledHeader.MobileAccordion key={idx}>
														<StyledHeader.MobileAccordionHeader
															onClick={() => onMobilePrimaryNavClick(idx)}
															aria-label={menu.Values.Title}
															aria-expanded={
																!!selectedMainMenu &&
																selectedMainMenu.Values.Title === menu.Values.Title
															}
															role="menu">
															<StyledHeader.MobileAccordionHeaderText>
																{menu.Values.Title}
															</StyledHeader.MobileAccordionHeaderText>
															<StyledHeader.IconChevronLeftStyled />
														</StyledHeader.MobileAccordionHeader>
														<StyledHeader.MobileAccordionDropdown>
															<StyledHeader.TextLinkWithArrowStyled
																title={menu.Values.DropdownArrowLink.Name}
																href={menu.Values.DropdownArrowLink.Url}
																target={menu.Values.DropdownArrowLink.Target}
																tabIndex={
																	!!selectedMainMenu &&
																	selectedMainMenu.Values.Title === menu.Values.Title
																		? 0
																		: -1
																}
																role="menuitem"
															/>
															{menu.Values.DropdownColumns.map((dropdownColumn, idx) => (
																<StyledHeader.DropdownSecondaryColumn key={idx}>
																	<StyledHeader.ColumnTitle>
																		{dropdownColumn.Values.Title}
																	</StyledHeader.ColumnTitle>
																	{dropdownColumn.Values.Links.map(
																		(link, linkIdx) => (
																			<StyledHeader.LinkWrapper key={linkIdx}>
																				<StyledHeader.Link
																					title={link.Name}
																					href={link.Url}
																					tabIndex={
																						!!selectedMainMenu &&
																						selectedMainMenu.Values
																							.Title === menu.Values.Title
																							? 0
																							: -1
																					}
																					role="menuitem"
																				/>
																			</StyledHeader.LinkWrapper>
																		)
																	)}
																</StyledHeader.DropdownSecondaryColumn>
															))}
														</StyledHeader.MobileAccordionDropdown>
													</StyledHeader.MobileAccordion>
												))}
												<StyledHeader.MobileSearchLink
													href={appFrame.Values.SearchLink.Url}
													title={appFrame.Values.SearchLink.Name}
													target={appFrame.Values.SearchLink.Target}
													icon={appFrame.Values.SearchLinkIcon}
													role="menuitem"
												/>
											</StyledHeader.DropdownTopSection>
											<StyledHeader.DropdownBottomSection>
												<StyledHeader.DropdownBottomDynamic>
													<StyledHeader.MyAccountLink
														href={appFrame.Values.MyAccountLink.Url}
														title={
															isLoggedIn && userProfile
																? `${t('myShipments:hi')} ${firstName(userProfile.name)}`
																: appFrame.Values.MyAccountLink.Name
														}
														target={appFrame.Values.MyAccountLink.Target}
														icon={appFrame.Values.MyAccountLinkIcon}
														role="menuitem"
													/>
													{appFrame.Values.HeaderMainMenu.map(
														(menu, idx) =>
															menu.Values.DropdownExtraLink && (
																<StyledHeader.DropdownBottomExtraLink
																	key={idx}
																	title={menu.Values.DropdownExtraLink.Name}
																	href={menu.Values.DropdownExtraLink.Url}
																	target={menu.Values.DropdownExtraLink.Target}
																	icon={menu.Values.DropdownExtraLinkIcon}
																	role="menuitem"
																/>
															)
													)}
												</StyledHeader.DropdownBottomDynamic>
												<StyledHeader.DropdownBottomStatic>
													{appFrame.Values.HeaderBottomLinks.map((link, idx) => (
														<StyledHeader.BottomLink
															key={idx}
															title={link.Values.Link.Name}
															href={link.Values.Link.Url}
															target={link.Values.Link.Target}
															icon={link.Values.Icon}
															role="menuitem"
														/>
													))}
												</StyledHeader.DropdownBottomStatic>
												{isLoggedIn && (
													<StyledHeader.LogoutLink
														onClick={() => {
															dispatch(logout());
															onMobileMenuCLick();
														}}>
														{t('navigation.logout')}
													</StyledHeader.LogoutLink>
												)}
											</StyledHeader.DropdownBottomSection>
										</>
									)}
								</StyledHeader.NavigationDropdown>
							</StyledHeader.MenuItemWrapper>
						)}
						{!smallDevice && selectedMainMenu && (
							<StyledHeader.NavigationDropdown
								onMouseEnter={onDropdownMouseEnter}
								onMouseLeave={onDropdownMouseLeave}
								role="menu">
								<StyledHeader.DropdownTopSection>
									<StyledHeader.DropdownPrimaryColumn>
										<RichTextTitle content={selectedMainMenu.Values.DropdownTitle} type="h5" />
										<StyledHeader.TextLinkWithArrowStyled
											title={selectedMainMenu.Values.DropdownArrowLink.Name}
											onKeyDown={onFirstFocusableDropdownRefKeyDown}
											href={selectedMainMenu.Values.DropdownArrowLink.Url || '#'}
											target={selectedMainMenu.Values.DropdownArrowLink.Target || '_self'}
											anchorRef={firstFocusableDropdownRef}
											role="menuitem"
										/>
									</StyledHeader.DropdownPrimaryColumn>
									<StyledHeader.DropdownSecondaryColumnWrapper>
										{selectedMainMenu.Values.DropdownColumns.map((dropdownColumn, idx) => (
											<StyledHeader.DropdownSecondaryColumn key={idx}>
												<StyledHeader.ColumnTitle>
													{dropdownColumn.Values.Title}
												</StyledHeader.ColumnTitle>
												{dropdownColumn.Values.Links.map((link, linkIdx) => (
													<StyledHeader.LinkWrapper key={linkIdx}>
														<StyledHeader.Link
															title={link.Name}
															href={link.Url}
															role="menuitem"
														/>
													</StyledHeader.LinkWrapper>
												))}
											</StyledHeader.DropdownSecondaryColumn>
										))}
									</StyledHeader.DropdownSecondaryColumnWrapper>
								</StyledHeader.DropdownTopSection>
								<StyledHeader.DropdownBottomSection>
									<StyledHeader.DropdownBottomStatic>
										{appFrame.Values.HeaderBottomLinks.map((link, idx) => (
											<StyledHeader.BottomLink
												key={idx}
												title={link.Values.Link.Name}
												href={link.Values.Link.Url}
												target={link.Values.Link.Target}
												role="menuitem"
												onKeyDown={
													!selectedMainMenu.Values.DropdownExtraLink &&
													idx === appFrame.Values.HeaderBottomLinks.length - 1
														? onLastFocusableDropdownRefKeyDown
														: null
												}
												icon={link.Values.Icon}
											/>
										))}
									</StyledHeader.DropdownBottomStatic>
									{selectedMainMenu.Values.DropdownExtraLink && (
										<StyledHeader.DropdownBottomExtraLink
											title={selectedMainMenu.Values.DropdownExtraLink.Name}
											href={selectedMainMenu.Values.DropdownExtraLink.Url}
											target={selectedMainMenu.Values.DropdownExtraLink.Target}
											icon={selectedMainMenu.Values.DropdownExtraLinkIcon}
											role="menuitem"
											onKeyDown={onLastFocusableDropdownRefKeyDown}
										/>
									)}
								</StyledHeader.DropdownBottomSection>
							</StyledHeader.NavigationDropdown>
						)}
					</PageContainerStyled>
				</StyledHeader.HeaderContainer>
			</StickyHeader>
			<AnnouncementBanner />
		</>
	);
};

export default Header;
